import { isApplicationSourceRevoked } from "@client/offline/artifactQueryPersistence";
import { APPLICATION_AVAILABILITY_QUERY_OPTIONS } from "@client/components/applications/applicationAvailability";
import { ApplicationDeadline } from "./ApplicationDeadline";
import { roundApplicationPath } from "@lib/roundSlugs";
import { OpenFundingOpportunities } from "./OpenFundingOpportunities";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { useImpersonationRefValue } from "@client/ImperativeState";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { Page } from "@client/components/utils/Page";
import { LoginPanel } from "@lib/auth/client/Login/LoginButton";
import { trpc } from "@client/trpc";
import { Button, Skeleton } from "@sfc/ui";
import { ApplicationLoading } from "./ApplicationLoading";
import { lazy, Suspense, useCallback, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "server";

// Keep the guide and its Markdown search pipeline out of the eager app shell.
const FormGuideView = lazy(async () => ({
  default: (await import("@client/pages/application-builder/FormGuideView"))
    .FormGuideView,
}));

type Application =
  inferRouterOutputs<AppRouter>["getPublicOpenApplications"][number];
export function OpenApplicationList({
  applications,
  layout = "list",
  showHeading = true,
}: {
  applications: Application[];
  layout?: "list" | "cards" | "directory";
  showHeading?: boolean;
}) {
  if (applications.length === 0) return null;
  return (
    <section
      aria-labelledby={showHeading ? "open-applications-heading" : undefined}
      className="w-full space-y-4"
    >
      {showHeading && (
        <div>
          <h2 id="open-applications-heading" className="heading-lg">
            Apply for Funding
          </h2>
          <p className="mt-2 body-md text-muted-foreground">
            Explore funding rounds for your team’s work.
          </p>
        </div>
      )}
      <ul
        className={
          layout === "directory"
            ? "@container min-w-0 space-y-3"
            : layout === "cards"
              ? "grid min-w-0 gap-4 sm:grid-cols-2"
              : "min-w-0 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card"
        }
      >
        {applications.map((application) => (
          <li
            key={application.roundId}
            className={
              layout === "cards" || layout === "directory"
                ? "overflow-hidden rounded-lg border border-border bg-card"
                : undefined
            }
          >
            {layout === "directory" ? (
              <div className="flex flex-col gap-4 p-5 @lg:flex-row @lg:items-center @lg:justify-between @lg:p-6">
                <div className="min-w-0 space-y-1">
                  <h3 className="heading-md [overflow-wrap:anywhere]">
                    {application.roundName}
                  </h3>
                  <p className="body-sm text-muted-foreground">
                    Accepting applications
                  </p>
                </div>
                <Button
                  nativeButton={false}
                  className="min-h-11 w-full @lg:w-auto"
                  render={<Link href={roundApplicationPath(application)} />}
                  aria-label={`Preview application: ${application.roundName}`}
                >
                  Preview application{" "}
                  <ArrowRight size={16} aria-hidden="true" />
                </Button>
              </div>
            ) : (
              <Link
                href={roundApplicationPath(application)}
                className="group flex h-full w-full flex-wrap items-start justify-start text-left gap-4 px-6 py-5 text-foreground transition-colors hover:bg-muted/40 hover:text-primary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
              >
                <span className="heading-sm w-full min-w-0 text-left [overflow-wrap:anywhere]">
                  {application.roundName}
                </span>
                <span
                  className={
                    layout === "cards"
                      ? "shrink-0 rounded-md bg-primary px-4 py-3 label-md text-primary-foreground"
                      : "label-md shrink-0 text-primary group-hover:underline underline-offset-4"
                  }
                >
                  Preview Application →
                </span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
type Preview = inferRouterOutputs<AppRouter>["getPublicApplicationPreview"];
type PrivatePreview = inferRouterOutputs<AppRouter>["getApplicationPreview"];

export function ApplicationPreview({
  application,
  viewerId,
}: {
  application: Preview | PrivatePreview;
  viewerId?: string;
}) {
  const [compact, setCompact] = useState(false);
  const observeHeaderStart = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const root = node.closest("[data-application-preview-scroll]");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry)
          setCompact(
            entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0),
          );
      },
      { root },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const data =
    !viewerId ||
    ("viewerId" in application && application.viewerId === viewerId)
      ? application
      : undefined;
  if (!data) return <ApplicationPreviewLoadingContent />;
  return (
    <div className="relative space-y-6">
      <div
        ref={observeHeaderStart}
        aria-hidden="true"
        className="absolute top-0 h-px w-px"
      />
      <header className="sticky top-0 z-20 -mx-6 flex min-h-20 items-center justify-between gap-4 border-b border-border bg-background px-6 py-4 md:-mx-10 md:px-10">
        <h1
          className={
            compact
              ? "heading-lg min-w-0 truncate"
              : "application-title min-w-0 break-words"
          }
          title={data.roundName}
        >
          {data.roundName}
        </h1>
        {(!viewerId || ("canApply" in data && data.canApply === true)) && (
          <Button
            nativeButton={false}
            className="min-h-11 shrink-0"
            render={<Link href={`${roundApplicationPath(data)}?apply=1`} />}
          >
            Apply
          </Button>
        )}
      </header>
      <p className="body-lg text-muted-foreground">
        Browse the application questions before you apply.
      </p>
      <ApplicationDeadline closesAt={data.applicationWindow?.closesAt} />
      <Suspense fallback={<ApplicationLoading layout="questions" />}>
        <FormGuideView content={data.content} />
      </Suspense>
    </div>
  );
}
export function PublicOpenApplications() {
  const query = trpc.getPublicOpenApplications.useQuery(undefined, {
    ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
    retry: false,
    gcTime: 0,
  });
  const applications = isApplicationSourceRevoked(query.error)
    ? undefined
    : query.data;
  if (query.isPending || applications?.length === 0) return null;
  return (
    <div className="mt-9 mb-3 w-full">
      {query.isError && applications === undefined ? (
        <div role="alert" className="space-y-3">
          <p>Couldn’t load open funding rounds.</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <OpenFundingOpportunities applications={applications ?? []} />
      )}
    </div>
  );
}

export function PublicRoundApplication({
  roundId,
  signingIn,
}: {
  roundId: string;
  signingIn: boolean;
}) {
  const query = trpc.getPublicApplicationPreview.useQuery(
    { roundId },
    {
      ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
      retry: false,
      gcTime: 0,
    },
  );
  const application = isApplicationSourceRevoked(query.error)
    ? undefined
    : query.data;
  if (query.isPending && !signingIn) return <ApplicationPreviewLoading />;
  if (application && !signingIn)
    return <ApplicationPreviewPage application={application} />;
  return (
    <Page className="mx-auto max-w-5xl space-y-6">
      <span hidden data-application-theme="green" />
      {query.isPending ? (
        <ApplicationLoading />
      ) : query.isError ? (
        <p role="alert">
          This application is unavailable, or you may need to sign in to access
          it.
        </p>
      ) : !application ? (
        <p>This round is not currently accepting applications.</p>
      ) : (
        <>
          <h1 className="application-title">{application.title}</h1>
        </>
      )}
      {(signingIn || query.isError) && (
        <section className="max-w-md space-y-6">
          <h2 className="heading-lg">Sign in to continue</h2>
          <LoginPanel />
        </section>
      )}
    </Page>
  );
}

export function ApplicationPreviewPage({
  application,
  viewerId,
}: {
  application: Preview | PrivatePreview;
  viewerId?: string;
}) {
  return (
    <ApplicationPreviewShell>
      <ApplicationPreview application={application} viewerId={viewerId} />
    </ApplicationPreviewShell>
  );
}

function ApplicationPreviewShell({ children }: { children: React.ReactNode }) {
  return (
    <RouteScroller data-application-preview-scroll>
      <main className="mx-auto w-full max-w-[1200px] space-y-8 px-6 py-8 md:px-10">
        <span hidden data-application-theme="green" />
        <Link
          href="/apply"
          className="inline-block label-md text-primary hover:underline underline-offset-4"
        >
          ← Funding Rounds
        </Link>
        {children}
      </main>
    </RouteScroller>
  );
}

export function ApplicationPreviewLoading() {
  return (
    <ApplicationPreviewShell>
      <ApplicationPreviewLoadingContent />
    </ApplicationPreviewShell>
  );
}

function ApplicationPreviewLoadingContent() {
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="w-full max-w-2xl space-y-3">
          <Skeleton
            className="h-9 w-3/4 motion-reduce:animate-none"
            aria-hidden="true"
          />
          <p className="body-lg text-muted-foreground">
            Browse the application questions before you apply.
          </p>
        </div>
      </header>
      <div className="grid items-start gap-8 lg:grid-cols-[14rem_minmax(0,1fr)]">
        <div className="space-y-3" aria-hidden="true">
          <Skeleton className="h-6 w-3/4 motion-reduce:animate-none" />
          <Skeleton className="h-6 w-2/3 motion-reduce:animate-none" />
        </div>
        <ApplicationLoading layout="questions" />
      </div>
    </div>
  );
}

export function SignedInFundingPage() {
  const { data: user } = useCurrentUser();
  const impersonation = useImpersonationRefValue();
  return (
    <FundingDirectory authenticated viewerId={impersonation ?? user?.userId} />
  );
}

export function FundingDirectory({
  authenticated = false,
  viewerId,
}: {
  authenticated?: boolean;
  viewerId?: string;
}) {
  const publicQuery = trpc.getPublicOpenApplications.useQuery(undefined, {
    enabled: !authenticated,
    ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
    retry: false,
    gcTime: 0,
  });
  const privateQuery = trpc.getOpenApplications.useQuery(undefined, {
    enabled: authenticated && !!viewerId,
    ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
    retry: false,
    gcTime: 0,
  });
  const query = authenticated ? privateQuery : publicQuery;
  const applications = isApplicationSourceRevoked(query.error)
    ? undefined
    : authenticated
      ? privateQuery.data?.viewerId === viewerId
        ? privateQuery.data?.applications
        : undefined
      : publicQuery.data;
  return (
    <RouteScroller>
      <main className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8 md:px-8 md:py-10">
        <span hidden data-application-theme="green" />
        <Link
          href="/"
          className="inline-flex min-h-11 items-center body-md text-primary hover:underline underline-offset-4"
        >
          ← Home
        </Link>
        <header className="space-y-3">
          <h1 className="heading-xl">Apply for Funding</h1>
          <p className="body-md text-muted-foreground">
            Preview an application, then choose a team to get started.
          </p>
        </header>
        <section aria-labelledby="funding-rounds-heading" className="space-y-3">
          <h2 id="funding-rounds-heading" className="heading-sm">
            Open Funding Rounds
          </h2>
          {query.isError && applications === undefined ? (
            <div role="alert" className="space-y-3">
              <p>Couldn’t load open funding rounds.</p>
              <Button variant="nav" onClick={() => void query.refetch()}>
                Try again
              </Button>
            </div>
          ) : applications === undefined ? (
            <ApplicationLoading
              label="Loading funding rounds"
              rows={2}
              layout="directory"
            />
          ) : applications.length === 0 ? (
            <p className="body-md text-muted-foreground">
              There are no open funding rounds right now. Please check back
              later.
            </p>
          ) : (
            <OpenApplicationList
              applications={applications}
              layout="directory"
              showHeading={false}
            />
          )}
        </section>
        {applications && applications.length > 0 && (
          <p className="body-sm text-muted-foreground">
            No sign-in needed to preview. Your team can edit and submit
            together.
          </p>
        )}
      </main>
    </RouteScroller>
  );
}
